import {getIsZoomInstallation} from '../../../_shared/GWTBridge/invoke/common';
import {common} from '@priority-software/priority-proto-api/lib';
import {defaultTabsData} from '../constants';
import {getUserLogin} from '../../../constants/metaData';
import {
    getServerStoreData,
    setServerStoreData
} from '../../../services/proto/ServerStorage/service';
import {getCompanyName} from '../../../utils/proto-api-utils/utils';

export type OnboardingState = {
    OB_isCompleted?: boolean;
    OB_isSkipped?: boolean;
    OB_step?: string;
    OB_questionnaire_step?: string;
    OB_questionnaire_completed?: boolean;
    OB_tabs?: string;
};

export type OB_Store_type = {
    [key: string]: common.IConstant;
};

const DEFAULT_STORE_MAP = {
    OB_isCompleted: new common.Constant({Boolean: false}),
    OB_isSkipped: new common.Constant({Boolean: false}),
    OB_step: new common.Constant({Str: ''}),
    OB_questionnaire_step: new common.Constant({Str: '0'}),
    OB_tabs: new common.Constant({Str: JSON.stringify(defaultTabsData).replace(/\\"/g, '"')}),
    OB_questionnaire_completed: new common.Constant({Boolean: false})
};

const getKeyName = () => `onboarding-${getCompanyName()}`;

export const showOnboardingDlg = async ref => {
    const onboardingUser = 'master';
    const params = new URLSearchParams(window.location.search);
    const isObDevelopment = params.get('_onboarding') === '1';

    const isZoom = getIsZoomInstallation();
    let isOldZoom = null;

    if (isZoom) {
        const isOldZoomData = await getServerStoreData(['OB:isOldZoom']);
        isOldZoom = mapDataByType(isOldZoomData?.['OB:isOldZoom']); // get the val from stored const
    }

    if (!isObDevelopment) {
        if (!isZoom || isOldZoom || getUserLogin()?.toLocaleLowerCase() !== onboardingUser) return;
    }

    // request all necessary data from the server
    await requestOnboardingState();
    // check if the OB is completed;
    const isComplete = await isOnboardingCompleted();

    if ((ref && isObDevelopment) || (ref && !isComplete)) {
        ref.showOnboardingDlg();
    }
};

export const closeOnboardingDlg = async ref => {
    if (ref) {
        ref.closeOnboardingDlg();
    }
};

export const resetOnboardingState = async () => {
    await setServerStoreData(DEFAULT_STORE_MAP, false, true);
};

export const isOnboardingCompleted = async () => {
    const state: OnboardingState = await getOnboardingState();

    return state.OB_isCompleted || state.OB_isSkipped;
};

const mapDataByType = (data: common.IConstant | null) => {
    if (data) {
        const entries = Object.entries(data);

        if (entries[0]) {
            const [key, value] = entries[0];

            if (key === 'Str') return `${value}`;

            if (key === 'Boolean') return Boolean(value);

            if (key === 'Int') return Number(value);

            return value;
        }
    }

    return null;
};

export const getClientLocalData = () => {
    const clientData = localStorage.getItem(getKeyName());
    return clientData ? (JSON.parse(clientData) as OB_Store_type) : null;
};

export const isQuestionnaireCompleted = async () => {
    const data = getClientLocalData();
    return mapDataByType(data.OB_questionnaire_completed);
};

export const setQuestionnaireCompleted = async () => {
    await setOnboardingState({OB_questionnaire_completed: new common.Constant({Boolean: true})});
};

export const getOnboardingState = (): OnboardingState => {
    const state = getClientLocalData();

    if (state) {
        return Object.keys(state).reduce(
            (prev, key) => ({...prev, [key]: mapDataByType(state[key])}),
            {}
        );
    }

    return {};
};

const setStoredData = async (StoreMap = {}) => {
    try {
        const res = await setServerStoreData(StoreMap, false, true);
        return res;
    } catch (e) {
        console.error(e);
    }
};

export const requestOnboardingState = async (): Promise<any> => {
    const storeKeys = Object.keys(DEFAULT_STORE_MAP);
    const storedData = await getServerStoreData(storeKeys);

    if (storedData && Object.keys(storedData).length > 0) {
        localStorage.setItem(getKeyName(), JSON.stringify(storedData));
    } else {
        await setServerStoreData(DEFAULT_STORE_MAP, false, true);
        localStorage.setItem(getKeyName(), JSON.stringify(DEFAULT_STORE_MAP));
    }
};

export const setOnboardingState = async (state: Partial<OB_Store_type> = {}) => {
    const localData = getClientLocalData() || {};

    const updatedData = {...localData, ...state};
    const strData = JSON.stringify(updatedData);

    localStorage.setItem(getKeyName(), strData);
    await setStoredData(updatedData);
};

export const skipOnboardingProcess = async () =>
    setOnboardingState({OB_isSkipped: new common.Constant({Boolean: true})});

export const setOnboardingCompleted = async () =>
    setOnboardingState({OB_isCompleted: new common.Constant({Boolean: true})});

export const setOnboardingStep = async (step: string) =>
    setOnboardingState({OB_step: new common.Constant({Str: step})});

export const setQuestionnaireStep = async (step: string) =>
    setOnboardingState({OB_questionnaire_step: new common.Constant({Str: step})});

export function getTabsData() {
    const fieldVal = getOnboardingState().OB_tabs;
    return JSON.parse(fieldVal);
}

export function setTabsDataToStore(tabsData: Array<any>) {
    const data = JSON.stringify(tabsData).replace(/\\"/g, '"');

    return setOnboardingState({OB_tabs: new common.Constant({Str: data})});
}

const fieldFormChanged = 'formDataChanged';

export const getFieldName = (key: string) => `${getCompanyName()}_ob_${key}`;

export function isFormDataChanged() {
    const field = getFieldName(fieldFormChanged);
    return window[field];
}

export function setDataChanged(flag: boolean) {
    const field = getFieldName(fieldFormChanged);
    window[field] = flag;
}
